import { Component, OnInit, EventEmitter, Output, OnDestroy } from "@angular/core";
import { ICollection } from "@gtmhub/models";
import { from, Subscription } from "rxjs";
import { INavItem, NavItemsGroupType } from "../../../../models/nav-items-list.models";
import { favoriteUXCResponseToNavItem } from "@webapp/navigation/utils/nav-items.util";
import { FavoritesService } from "@gtmhub/favorites/favorites.service";
import { FavoritesFactory } from "@gtmhub/favorites/favorites-factory";
import { UXCustomizationType } from "@gtmhub/uxcustomization/models";
import { IFavoritesItemsGroupedResponse } from "@gtmhub/favorites/models";
import { map } from "rxjs";

@Component({
  selector: "gh-home-sub-navigation",
  templateUrl: "./home-sub-navigation.component.html",
})
export class HomeSubNavigationComponent implements OnInit, OnDestroy {
  @Output() titleChange = new EventEmitter<string>();
  public sessions: ICollection<INavItem>;
  public kpis: ICollection<INavItem>;
  public whiteboards: ICollection<INavItem>;
  public tasks: ICollection<INavItem>;
  public lists: ICollection<INavItem>;
  public insightboads: ICollection<INavItem>;
  public people: ICollection<INavItem>;
  public visibleItemsCount = 3;
  public itemsGroupType = "favorites" as NavItemsGroupType;

  private sectionTitle = "Home";
  private sessionsSubscription$: Subscription;
  private listsSubscription$: Subscription;
  private whiteboardsSubscription$: Subscription;
  private tasksSubscription$: Subscription;
  private insightboardsSubscription$: Subscription;
  private peopleSubscription$: Subscription;
  private kpisSubscription$: Subscription;

  constructor(private favoritesFactory: FavoritesFactory, protected favoritesService: FavoritesService) {}

  ngOnInit(): void {
    this.titleChange.emit(this.sectionTitle);

    this.sessionsSubscription$ = this.getSessions().subscribe((collection) => {
      this.sessions = collection;
    });

    this.listsSubscription$ = this.getLists().subscribe((collection) => {
      this.lists = collection;
    });

    this.whiteboardsSubscription$ = this.getWhiteboards().subscribe((collection) => {
      this.whiteboards = collection;
    });

    this.tasksSubscription$ = this.getTasks().subscribe((collection) => {
      this.tasks = collection;
    });

    this.kpisSubscription$ = this.getKPIs().subscribe((collection) => {
      this.kpis = collection;
    });

    this.insightboardsSubscription$ = this.getInsightboards().subscribe((collection) => {
      this.insightboads = collection;
    });

    this.peopleSubscription$ = this.getPeople().subscribe((collection) => {
      this.people = collection;
    });
  }
  ngOnDestroy(): void {
    this.sessionsSubscription$.unsubscribe();
    this.listsSubscription$.unsubscribe();
    this.whiteboardsSubscription$.unsubscribe();
    this.tasksSubscription$.unsubscribe();
    this.kpisSubscription$.unsubscribe();
    this.insightboardsSubscription$.unsubscribe();
    this.peopleSubscription$.unsubscribe();
  }

  private getFavorites() {
    const allTypes: UXCustomizationType[] = ["session", "kpigroup", "task", "whiteboard", "list", "dashboard", "user", "team"];
    return from(this.favoritesService.getFavoriteItems(this.favoritesFactory.buildNavigationFavoritesRequestParams(allTypes)));
  }

  private getSessions() {
    return this.getFavorites().pipe(
      map((data: IFavoritesItemsGroupedResponse) => {
        const sessionCollection: ICollection<INavItem> = {
          items: data.sessionItems ? data.sessionItems.map((sessionItem) => favoriteUXCResponseToNavItem(sessionItem.session, "session", sessionItem)) : [],
          totalCount: data.sessionItems ? data.sessionItems.length : 0,
        };
        return sessionCollection;
      })
    );
  }

  private getLists() {
    return this.getFavorites().pipe(
      map((data: IFavoritesItemsGroupedResponse) => {
        const listCollection: ICollection<INavItem> = {
          items: data.listItems ? data.listItems.map((listItem) => favoriteUXCResponseToNavItem(listItem.list, "list", listItem)) : [],
          totalCount: data.listItems ? data.listItems.length : 0,
        };
        return listCollection;
      })
    );
  }

  private getWhiteboards() {
    return this.getFavorites().pipe(
      map((data: IFavoritesItemsGroupedResponse) => {
        const whiteboardCollection: ICollection<INavItem> = {
          items: data.whiteboardItems
            ? data.whiteboardItems.map((whiteboardItem) => favoriteUXCResponseToNavItem(whiteboardItem.whiteboard, "whiteboard", whiteboardItem))
            : [],
          totalCount: data.whiteboardItems ? data.whiteboardItems.length : 0,
        };
        return whiteboardCollection;
      })
    );
  }

  private getTasks() {
    return this.getFavorites().pipe(
      map((data: IFavoritesItemsGroupedResponse) => {
        const tasksCollection: ICollection<INavItem> = {
          items: data.taskItems ? data.taskItems.map((taskItem) => favoriteUXCResponseToNavItem(taskItem.task, "task", taskItem)) : [],
          totalCount: data.taskItems ? data.taskItems.length : 0,
        };
        return tasksCollection;
      })
    );
  }

  private getInsightboards() {
    return this.getFavorites().pipe(
      map((data: IFavoritesItemsGroupedResponse) => {
        const insightboardsCollection: ICollection<INavItem> = {
          items: data.dashboardItems ? data.dashboardItems.map((dashboardItem) => favoriteUXCResponseToNavItem(dashboardItem.dashboard, "dashboard", dashboardItem)) : [],
          totalCount: data.dashboardItems ? data.dashboardItems.length : 0,
        };
        return insightboardsCollection;
      })
    );
  }

  private getKPIs() {
    return this.getFavorites().pipe(
      map((data: IFavoritesItemsGroupedResponse) => {
        const kpiGroupsCollection: ICollection<INavItem> = {
          items: data.kpiGroupsItems ? data.kpiGroupsItems.map((kpiGroupItem) => favoriteUXCResponseToNavItem(kpiGroupItem.kpiGroup, "kpigroup", kpiGroupItem)) : [],
          totalCount: data.kpiGroupsItems ? data.kpiGroupsItems.length : 0,
        };
        return kpiGroupsCollection;
      })
    );
  }

  private getPeople() {
    return this.getFavorites().pipe(
      map((data: IFavoritesItemsGroupedResponse) => {
        const usersCollection: ICollection<INavItem> = {
          items: data.userItems ? data.userItems.map((userItem) => favoriteUXCResponseToNavItem(userItem.user, "user", userItem)) : [],
          totalCount: data.userItems ? data.userItems.length : 0,
        };
        const teamsCollection: ICollection<INavItem> = {
          items: data.teamItems ? data.teamItems.map((teamItem) => favoriteUXCResponseToNavItem(teamItem.team, "team", teamItem)) : [],
          totalCount: data.teamItems ? data.teamItems.length : 0,
        };

        return {
          items: [...usersCollection.items, ...teamsCollection.items],
          totalCount: usersCollection.totalCount + teamsCollection.totalCount,
        };
      })
    );
  }
}
